import { useEffect, useMemo, useState } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { Autocomplete, TextField } from '@mui/material'
import { range } from 'lodash'

import { DefaultElementProps } from 'common/interfaces'
import InputLabel from 'components/InputLabel'
import { SizingContainer } from 'components/SizingContainer'
import { useTemplate } from 'utils/hook'
import validationsToRules from 'utils/validationsToRules'

import { YearPicker as YearPickerProps } from './interface'

export default function YearPicker({
  name,
  validations,
  defaultValue,
  onFocus,
  onChange,
  startYear,
  endYear,
  delta = 100,
  label,
  size,
}: YearPickerProps & DefaultElementProps): JSX.Element {
  const render = useTemplate()

  const { control, setError, clearErrors } = useFormContext()
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    rules: validationsToRules(validations),
    defaultValue: render(defaultValue ?? ''),
  })

  const [yearValue, setYearValue] = useState(field.value ?? render(defaultValue ?? ''))

  const yearOptions = useMemo(() => {
    if (startYear === undefined && endYear === undefined) {
      const yearTo = new Date().getFullYear()
      const yearFrom = yearTo - delta

      return range(yearFrom, yearTo).map(String)
    }

    if (startYear !== undefined && endYear !== undefined) {
      const yearFrom = Number(render(startYear))
      const yearTo = Number(render(endYear))

      return range(yearFrom, yearTo).map(String)
    }

    if (startYear !== undefined && endYear === undefined) {
      const yearFrom = Number(render(startYear))
      const yearTo = yearFrom + delta

      return range(yearFrom, yearTo).map(String)
    }

    if (startYear === undefined && endYear !== undefined) {
      const yearTo = Number(render(endYear))
      const yearFrom = yearTo - delta

      return range(yearFrom, yearTo).map(String)
    }
  }, [])

  useEffect(() => {
    if (!yearOptions!.includes(yearValue) && yearValue !== '') {
      setError(name, { message: 'Fill correct year, please' })
    } else {
      clearErrors(name)
    }
  }, [yearValue])

  return (
    <SizingContainer className="year-picker-wrap" size={size} minWidth={150}>
      <Autocomplete
        {...field}
        value={yearValue}
        onChange={(e, value: string) => {
          setYearValue(value)
          field.onChange(value)
          onChange?.({ value })
        }}
        freeSolo
        options={yearOptions!}
        renderInput={(params) => {
          const errorMessageId = `${name}-helper-text`
          return (
            <TextField
              {...params}
              id={name}
              required={validations.required?.value}
              label={<InputLabel label={label} />}
              type="number"
              onFocus={onFocus}
              onChange={(e) => {
                setYearValue(e.target.value)
                field.onChange(e)
                onChange?.({ value: e.target.value })
              }}
              error={error !== undefined}
              helperText={error?.message}
              inputProps={{
                ...params.inputProps,
                'aria-invalid': error !== undefined,
                'aria-describedby': error !== undefined ? errorMessageId : undefined,
              }}
              FormHelperTextProps={{
                id: errorMessageId,
                role: error !== undefined ? 'alert' : undefined,
              }}
            />
          )
        }}
      />
    </SizingContainer>
  )
}
